import { BaseInput, Button, ButtonVariant } from "shared/components";
import { tenDigitTelParser } from "shared/components/forms/BaseInput/parsers";
import { CombinedIncomeLayout } from "../components/Layout";
import { toast } from "react-toastify";
import { CombinedIncomeSubStep } from "../../../constants";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { object, string } from "yup";
import { useCombinedIncome } from "../context";
import { getErrorMessage } from "utils/error";

type SupervisorDetailsFormData = {
  first_name: string;
  last_name: string;
  contact_email: string;
  contact_phone: string;
};

const validationSchema = object({
  first_name: string().required("Supervisor first name is required"),
  last_name: string().required("Supervisor last name is required"),
  contact_email: string().email("Please enter a valid email"),
  contact_phone: string().required("Supervisor phone is required")
});

export const SupervisorDetailsStep = () => {
  const { updateStepMutation, stepData, getNextStep, refetchVerification } =
    useCombinedIncome();

  const { register, onSubmit } = useFormTyped<SupervisorDetailsFormData>(
    {
      first_name: stepData.supervisor?.first_name || "",
      last_name: stepData.supervisor?.last_name || "",
      contact_email: stepData.supervisor?.contact_email || "",
      contact_phone: stepData.supervisor?.contact_phone || ""
    },
    validationSchema
  );

  const handleSubmit = (formValues: SupervisorDetailsFormData) => {
    const nextStep = getNextStep(CombinedIncomeSubStep.CompanyDetails, {
      incomeType: stepData.currentIncomeType
    });
    if (!nextStep) return;

    updateStepMutation.mutate(
      {
        lastStep: CombinedIncomeSubStep.PreTaxIncome,
        supervisor: {
          first_name: formValues.first_name,
          last_name: formValues.last_name,
          contact_email: formValues.contact_email,
          contact_phone: formValues.contact_phone
        }
      },
      {
        onSuccess: () => {
          refetchVerification();
        },
        onError: (error) => {
          toast.error(
            getErrorMessage(error) || "Failed to save supervisor details"
          );
        }
      }
    );
  };

  return (
    <form onSubmit={onSubmit({ onSuccess: handleSubmit })}>
      <CombinedIncomeLayout
        title="Add supervisor details"
        footer={
          <Button
            className="w-[132px]"
            type="submit"
            loading={updateStepMutation.isPending}
            variant={ButtonVariant.PRIMARY}
          >
            Continue
          </Button>
        }
      >
        <BaseInput
          {...register("first_name")}
          id="supervisor-first-name"
          label="Supervisor first name"
          placeholder="John"
          labelClassName="mt-4"
          required
        />

        <BaseInput
          {...register("last_name")}
          id="supervisor-last-name"
          label="Supervisor last name"
          placeholder="Doe"
          labelClassName="mt-4"
          required
        />

        <BaseInput
          {...register("contact_email")}
          id="supervisor-email"
          label="Supervisor email"
          type="email"
          placeholder="john.doe@example.com"
          labelClassName="mt-4"
        />

        <BaseInput
          {...register("contact_phone")}
          id="supervisor-phone"
          label="Supervisor phone"
          parser={tenDigitTelParser({ asString: true })}
          placeholder="(123) 000-0000"
          labelClassName="mt-4"
          required
        />
      </CombinedIncomeLayout>
    </form>
  );
};
